import React, { useState, useEffect } from 'react';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { useKeycloak } from '@react-keycloak/web';
import { eraseCookie } from '../ckcore/core/CkUtils';
import User from '../ckcore/core/User';
import Config from '../ckcore/core/Config';
import TxStatus from '../ckcore/core/TxStatuses';
import TxTypes from '../ckcore/core/TxTypes';

import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';

import SignOut from '../ckcore/account/SignOut';
import ProfileDialog from './home/ProfileDialog';
import CkAppBar from './home/CkAppBar';
import CkApps from './home/CkApps';
import SettingDialog from './home/SettingDialog';
import PasswordDialog from './home/PasswordDialog';

import '../../App.css';
import DashboardTheme from './DashboardTheme';

// Custom Dashboard Title
if (window.ckDashboardTitle) {
  document.title = window.ckDashboardTitle;
}

// Update dashboard title function
const updateDashboardTitle = (partnerName) => {
  document.title = partnerName + " - Chuyển Khoản Dashboard";
};

export default function Dashboard(props) {
  const classes = useStyles();
  const { keycloak } = useKeycloak();
  const [loggedIn, setLoggedIn] = useState(keycloak.authenticated);

  const [userObj, setUserObj] = useState({});
  const [partnerObj, setPartnerObj] = useState({});
  const [selectedObj, setSelectedObj] = useState({ appId: '', action: '', refreshTime: 0 });

  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showSettingDialog, setShowSettingDialog] = useState(false);

  // Logout Handling
  const handleLogout = (fullLogout) => {
    setShowLogoutDialog(false);
    if (fullLogout && keycloak.authenticated) {
      eraseCookie(User.getDisplayPhone() + '_token');
      // Redirect to the current environment (dev / stg / live) after logout
      const redirectUri = window.location.origin + '/';
      keycloak.logout({ redirectUri });
    }
    eraseCookie("user_session");
    setLoggedIn(false);
  };

  const openLogoutDialog = () => setShowLogoutDialog(true);
  const closeLogoutDialog = () => setShowLogoutDialog(false);

  // Callback functions
  const showApp = (appId, action = '') => {
    if (appId === "PROFILE") setShowProfileDialog(true);
    else if (appId === "PASSWORD") setShowPasswordDialog(true);
    else if (appId === "SETTING") setShowSettingDialog(true);
    else {
      setSelectedObj({ appId, action, refreshTime: Date.now() });
    }
  };

  const refreshCallback = (appId, action = '') => {
    if (!appId) loadPartnerData();
    else showApp(appId, action);
  };

  const switchPartnerCallback = (newPartnerId) => switchPartner(newPartnerId);

  const closeProfileDialog = () => { setShowProfileDialog(false); loadPartnerData(); };
  const closePasswordDialog = () => { setShowPasswordDialog(false); loadPartnerData(); };
  const closeSettingDialog = () => { setShowSettingDialog(false); loadPartnerData(); };

  // Load partner data
  const loadPartnerData = async () => {
    const ckmsg = {
      txTypeId: TxTypes.PARTNER_ADMIN_INFO_LOAD,
      userId: User.getUserId(),
      partnerId: User.getActivePartner(),
      timestamp: Date.now()
    };
    try {
      const res = await fetch(Config.getServerPartnerURL("load"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": "Bearer " + keycloak.token
        },
        body: JSON.stringify(ckmsg),
      });
      const response = await res.json();

      if ([TxStatus.SUCCESS, TxStatus.HTTP_SUCCESS].includes(response.statusId)) {
        if (response.user) {
          setUserObj(response.user);

          if (Array.isArray(response.user.roleList)) {
            response.user.roles = response.user.roleList;
          }
        }

        setPartnerObj(response.partner);
        updateDashboardTitle(response.partner.name || '');

        if (!selectedObj.appId) {
          const defaultApp = User.hasActivePartner(response.user) ? "HOME" : "MEMBERS";
          setSelectedObj({ appId: defaultApp, refreshTime: Date.now() });
        } else {
          setSelectedObj(prev => ({ ...prev, refreshTime: Date.now() }));
        }

        if (!User.getEmail() && response.user.email) {
          User.setEmail(response.user.email);
        }
      } else {
        handleLogout(true);
      }
    } catch (error) {
      console.error("Error loadPartnerData:", error);
      handleLogout(true);
    }
  };

  // Switch Partner
  const switchPartner = async (newPartnerId) => {
    const ckmsg = {
      txTypeId: TxTypes.PARTNER_SWITCH,
      userId: User.getUserId(),
      partnerId: newPartnerId,
      timestamp: Date.now()
    };
    try {
      const res = await fetch(Config.getServerPartnerURL("switch"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${keycloak.token}`
        },
        body: JSON.stringify(ckmsg)
      });
      const result = await res.json();

      if (result.statusId === TxStatus.SUCCESS) {
        User.setActivePartner(newPartnerId);
        setUserObj(prev => ({ ...prev, partnerId: newPartnerId }));
        await loadPartnerData();
        setSelectedObj({ appId: "HOME", refreshTime: Date.now() });
      } else {
        alert(`Switch partner thất bại: ${result.message}`);
      }
    } catch (error) {
      console.error("Error switchPartner:", error);
    }
  };

  // Keycloak Token & Refresh Handling
  useEffect(() => {
    if (keycloak.authenticated && keycloak.tokenParsed) {
      const tokenData = keycloak.tokenParsed;
      User.setEmail(tokenData.email);
      User.setSessionId(keycloak.token);
      User.setUserId(parseInt(tokenData.user_id, 10));
      User.setActivePartner(parseInt(tokenData.active_partner_id, 10));
      User.setGroups(tokenData.groups);

      setUserObj({
        username: tokenData.preferred_username,
        email: tokenData.email,
        phone: tokenData.phone_number,
        tokenParsed: tokenData,
        name: tokenData.name,
        userId: User.getUserId(),
        partnerId: User.getActivePartner()
      });
      setLoggedIn(true);
    } else {
      setLoggedIn(false);
    }

    keycloak.onAuthRefreshError = () => {
      console.error("Keycloak refresh token failed => Logging out");
      handleLogout(true);
    };

  }, [keycloak.authenticated, keycloak.tokenParsed]);

  useEffect(() => { if (loggedIn) loadPartnerData(); }, [loggedIn]);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={DashboardTheme}>
        {loggedIn && !selectedObj.appId && <Box>Đang tải..</Box>}
        {loggedIn && selectedObj.appId &&
          <Box className={classes.root}>
            <CkAppBar {...{ userObj, partnerObj, showApp, openLogoutDialog, refreshCallback, switchPartnerCallback }} />
            <CkApps {...{ userObj, partnerObj, selectedAppId: selectedObj.appId, action: selectedObj.action, refreshTime: selectedObj.refreshTime, showApp, logoutCallback: handleLogout, refreshCallback }} />
            {showLogoutDialog && <SignOut open={showLogoutDialog} closeCallback={closeLogoutDialog} logoutCallback={handleLogout} />}
            {showProfileDialog && <ProfileDialog open={showProfileDialog} closeCallback={closeProfileDialog} {...{ userObj, partnerObj, selectedAppId: selectedObj.appId, logoutCallback: handleLogout, refreshCallback }} />}
            {showPasswordDialog && <PasswordDialog open={showPasswordDialog} closeCallback={closePasswordDialog} {...{ userObj, partnerObj, selectedAppId: selectedObj.appId, logoutCallback: handleLogout, refreshCallback }} />}
            {showSettingDialog && <SettingDialog open={showSettingDialog} closeCallback={closeSettingDialog} {...{ userObj, partnerObj, selectedAppId: selectedObj.appId, logoutCallback: handleLogout, refreshCallback }} />}
          </Box>
        }
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    height: "100%",
    width: "100%",
  }
}));
